<template>
  <div
    v-if="contentAnnotation"
    ref="editorContainer"
    class="annotation-content absolute inset-0 overflow-hidden cursor-crosshair flex flex-auto self-start h-full"
    :class="{ 'pointer-events-none': !contentAnnotation.activeTool }"
    @click="contentAnnotation.showMenu(false)"
  ></div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, onBeforeUnmount } from 'vue'
import { useElementSize, useDebounceFn } from '@vueuse/core'
import type { MarkerArea as MarkerAreaType } from '@markerjs/markerjs3'
import { useMarkerjs3 } from '@collection-player/composables/markerjs.use.ts'
import useCanvas from '@canvas-builder/composables/useCanvas.ts'
import useCanvasPages from '@canvas-builder/composables/useCanvasPages.ts'
import { cloneDeep } from 'lodash-es'

const emit = defineEmits<{
  repaint: []
}>()
const { contentAnnotation } = useCanvas()
const editorContainer = ref<HTMLDivElement | null>(null)
const editor = ref<MarkerAreaType | null>(null)
const imageElement = document.createElement('img')
const { width, height } = useElementSize(editorContainer)
const { currentPageIndex, activeSectionId } = useCanvasPages()

// Detects when MarkerJs component is disconnected. Emits repaint to recreate Vue component.
// Happens when component is moved (e.g. reordered), but Vue keeps the component instance (the same key property),
// then MarkerJs disconnects and connects again, but with defects. Manual repaint fixes MarkerJs issue.
const repaintOnDisconnectedCallback = (markerArea: MarkerAreaType) => {
  // @ts-expect-error access private property
  const originalDetachEvents = markerArea.detachEvents

  function detachEvents() {
    originalDetachEvents.call(markerArea)
    emit('repaint')
  }

  // @ts-expect-error access private property
  markerArea.detachEvents = detachEvents.bind(markerArea)
}

onMounted(async () => {
  if (!contentAnnotation.value) {
    return console.warn('Content Annotation is not initialized')
  }

  if (!editorContainer.value) {
    return
  }

  const { applyShadowRootStyles, createNewImage } = contentAnnotation.value

  const {
    markerjs3: { MarkerArea },
    laserPointer: { LaserPointerMarker, LaserPointerMarkerEditor },
  } = await useMarkerjs3()

  editor.value = new MarkerArea()

  // disable changing tool to selection on double click
  editor.value.addEventListener(
    'dblclick',
    (e) => {
      e.stopImmediatePropagation()
      e.preventDefault()
    },
    { capture: true },
  )

  editor.value.targetImage = imageElement

  // Create image to allow library to add proper classes
  editor.value.targetImage.src = createNewImage(1, 1)
  editor.value.registerMarkerType(LaserPointerMarker, LaserPointerMarkerEditor)

  repaintOnDisconnectedCallback(editor.value)
  if (editor.value.shadowRoot) {
    applyShadowRootStyles(editor.value.shadowRoot)
  }

  editorContainer.value.appendChild(editor.value)
  contentAnnotation.value?.setActiveMarker?.(editor.value)
})

async function scaleMarkerArea(markerInstance: MarkerAreaType, containerWidth: number, containerHeight: number) {
  const PADDING = 0
  if (!markerInstance?.targetImage || !contentAnnotation.value) return

  const { createNewImage } = contentAnnotation.value

  const oldWidth = markerInstance.targetWidth
  const oldHeight = markerInstance.targetHeight

  const availableWidth = containerWidth - PADDING * 2
  const availableHeight = containerHeight - PADDING * 2

  markerInstance.targetImage.src = createNewImage(width.value, height.value)

  const imageAspectRatio = width.value / height.value
  let newWidth, newHeight

  if (availableWidth / availableHeight > imageAspectRatio) {
    newHeight = availableHeight
    newWidth = newHeight * imageAspectRatio
  } else {
    newWidth = availableWidth
    newHeight = newWidth / imageAspectRatio
  }

  const scaleX = newWidth / oldWidth
  const scaleY = newHeight / oldHeight

  // @ts-expect-error fix scaling after image change
  markerInstance.scaleMarkers(scaleX, scaleY)
  markerInstance.targetWidth = newWidth
  markerInstance.targetHeight = newHeight
}

const debouncedHandleDimensionChange = useDebounceFn(() => {
  if (width.value === 0 || height.value === 0) return

  if (editor.value) {
    scaleMarkerArea(editor.value, width.value, height.value)
  }

  if (!contentAnnotation.value || !activePage) return

  // Try to restore annotations for current page after dimensions have stabilized
  const { storage, resetAnnotations } = contentAnnotation.value

  if (storage.has(activePage)) {
    const state = cloneDeep(storage.get(activePage))
    const isRestored = restoreAnnotations(state)

    if (!isRestored) {
      storage.delete(activePage)
    }
  } else {
    resetAnnotations()
  }
}, 50)

watch([width, height], () => {
  debouncedHandleDimensionChange()
})

let activePage: string | null = null

const restoreAnnotations = (state: any) => {
  if (!contentAnnotation.value) return false

  const { resetAnnotations, activeMarker, triggerToolUpdate } = contentAnnotation.value

  // Check if dimensions are close enough to restore state
  if (Math.abs(width.value - state.width) <= 1 && Math.abs(height.value - state.height) <= 1) {
    activeMarker?.restoreState(state)
    triggerToolUpdate()

    return true
  } else {
    resetAnnotations()

    return false
  }
}

watch(
  () => `${currentPageIndex.value}:${activeSectionId.value}`,
  (newPage, oldPage) => {
    if (!contentAnnotation.value) return

    activePage = newPage

    const { storage, resetAnnotations, activeMarker } = contentAnnotation.value

    if (!activeMarker) return

    if (oldPage) {
      storage.set(oldPage, cloneDeep(activeMarker.getState()))
    }

    if (storage.has(newPage)) {
      const state = cloneDeep(storage.get(newPage))
      // For page changes, restore immediately (not debounced)
      restoreAnnotations(state)
    } else {
      resetAnnotations()
    }
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  contentAnnotation.value?.clearStorage()
})
</script>
